import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Icon, Switch } from '@material-ui/core';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { colors } from '@/styles';
import { getShapedIconSvgURLString } from '@/components/DispatchPlan/map/MapMarkers';
import { createRoot } from 'react-dom/client';
import { Tooltip } from '@material-ui/core';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { PrimaryButtonFullW, PrimaryButton } from '@/components/DispatchPlan/blocks';
import { OnwardToggle } from '@/components/ShipmentForm/blocks';
import _ from 'lodash';
import Combination from './Combination';
import { renderShapeFieldTypeMenuItems } from './ShapeFieldTypeMenuItems';

const controlContainer = document.createElement('div');
const id = 'manufacturer-shape-control-container';
controlContainer.setAttribute('id', id);
const containerRoot = createRoot(controlContainer);

const MarkerIcon = ({ text, ...props }) => {
    return (
        <div
            css={css`
                position: relative;
            `}
        >
            <Icon
                {...props}
                css={css`
                    width: fit-content !important;
                    height: fit-content !important;
                `}
            >
                <img alt src={getShapedIconSvgURLString('black', props.shape)} width={27} height={33} />
            </Icon>
            <IconText
                css={css`
                    position: absolute;
                    left: 0;
                    top: ${text?.length === 1 ? '20%' : '25%'};
                    width: 27px;
                    text-align: center;
                    font-size: ${text?.length === 1 ? '14px' : '11px'};
                    font-weight: ${text?.length === 1 ? '700' : '500'};
                `}
            >
                {text}
            </IconText>
        </div>
    );
};

export const ShapeSelect = ({ entry, shapesOverride, setShapesOverride, showComboShapes, changeComboShape }) => {
    const [selectedShape, setSelectedShape] = useState('default');

    useEffect(() => {
        setSelectedShape(entry.shape || 'default');
    }, [entry.shape]);

    const handleChange = (event) => {
        if (showComboShapes) {
            changeComboShape(event);
        } else {
            const idx = (shapesOverride || []).findIndex((el) => el.name === entry.name);
            if (idx < 0) return;

            // Spread the entry rather than rebuilding it from name and shape: the row renders
            // `entry.label || entry.name`, so dropping the label made a shipper's name turn into
            // its uuid the moment its shape was changed.
            const override = [...shapesOverride];
            override[idx] = { ...entry, shape: event.target.value };

            setShapesOverride(override);
            setSelectedShape(event.target.value);
        }
    };

    const shapes = [
        'square',
        'circle',
        'triangle',
        'pentagon',
        'star',
        'cross',
        'rectangle',
        'diamond',
        'heart',
        'abstract1',
        'abstract2',
        'abstract3',
        'abstract4',
        'abstract5',
        'abstract6',
        'abstract7',
        'abstract8',
        'abstract9',
        'abstract10',
        'abstract11',
        'abstract12',
        'abstract13',
    ];

    return (
        <FormControl style={{ width: showComboShapes ? '100%' : '60%' }}>
            <Select
                css={css`
                    height: 57px;
                `}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedShape}
                onChange={handleChange}
                MenuProps={{
                    MenuListProps: {
                        sx: {
                            'li.MuiButtonBase-root': {
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        },
                    },
                }}
            >
                <MenuItem key={'default'} value={'default'}>
                    <MarkerIcon shape={'default'} />
                </MenuItem>
                {shapes.map((shape) => (
                    <MenuItem key={shape} value={shape}>
                        <MarkerIcon shape={shape} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const Body1 = styled.span`
    font-size: 14px;
    font-weight: 700;
`;

const Body2 = styled.span`
font-size: 14px;
font-weight: 500;p
`;

const IconText = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: white;
`;

const ShapeControlContent = ({
    shapesOverride,
    saveShapes,
    setShapesOverride,
    shapeFieldType,
    setShapeFieldType,
    shapeFieldTypeOptions,
    savedShapes,
    setLockShapeType,
    comboPotentialChoices,
    showComboShapes,
    setShowComboShapes,
    setComboShapesOverride,
    comboShapesOverride,
}) => {
    const [open, setOpen] = useState(false);
    const [updatedCombinations, setUpdatedCombinations] = useState(comboShapesOverride || []);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleFieldChange = (e) => {
        setShapeFieldType(e.target.value);
    };

    const handleSave = () => {
        // The list only holds groups present on the current board, so a straight replace wiped
        // every assignment for a shipper, tag or manufacturer that happened to have no orders
        // today. Keep the saved entries this list does not cover.
        const assigned = (shapesOverride || []).filter((el) => el.shape !== undefined);
        const assignedNames = new Set(assigned.map((el) => el.name));
        const untouched = (savedShapes?.[shapeFieldType] || []).filter((el) => !assignedNames.has(el.name));

        let newShapes = {
            ...savedShapes,
            [`${shapeFieldType}`]: [...assigned, ...untouched],
        };
        if (updatedCombinations) {
            newShapes.combinations = updatedCombinations;
            setComboShapesOverride(updatedCombinations);
        }
        setLockShapeType(true);
        // Only what the board actually covers: the merged array is for persistence, and feeding
        // the off-board entries back would list them, unlabelled, the next time this opens.
        setShapesOverride(assigned);
        saveShapes(newShapes);
        handleClose();
    };

    const addCombo = () => {
        setUpdatedCombinations([...updatedCombinations, { [shapeFieldTypeOptions[0]]: null, shape: 'default' }]);
    };

    const removeCombo = (index) => {
        const updated = [...updatedCombinations];
        updated.splice(index, 1);
        setUpdatedCombinations(updated);
    };

    return (
        <>
            <Tooltip title="Pin Customization" placement="top">
                <div
                    css={css`
                        margin: 10px;
                        width: 40px;
                        height: 40px;
                        background-color: ${colors.greens.primary};
                        border-radius: 2px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        cursor: pointer;
                    `}
                    onClick={handleOpen}
                >
                    <CategoryOutlinedIcon
                        css={css`
                            color: white;
                        `}
                    />
                </div>
            </Tooltip>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                    <Body1
                        css={css`
                            font-size: 20px;
                        `}
                    >
                        Customize Pins
                    </Body1>
                </DialogTitle>
                <DialogContent
                    css={css`
                        width: 500px;
                        max-height: 500px;
                    `}
                >
                    <div
                        css={css`
                            border-bottom: 1px solid lightgrey;
                            margin-left: -1.5rem;
                            margin-right: -1.5rem;
                            margin-bottom: 1rem;
                            padding-left: 1.5rem;
                            padding-bottom: 1rem;
                        `}
                    >
                        <Body2
                            css={css`
                                margin-right: 0.5rem;
                            `}
                        >
                            Single
                        </Body2>
                        <OnwardToggle
                            value={showComboShapes}
                            onChange={() => {
                                setShowComboShapes(!showComboShapes);
                            }}
                            css={css`
                                margin-left: 1rem;
                            `}
                        />
                        <Body2
                            css={css`
                                margin-left: 0.5rem;
                            `}
                        >
                            Combo
                        </Body2>
                    </div>
                    {showComboShapes ? (
                        <>
                            {updatedCombinations?.map((combo, index) => (
                                <Combination
                                    combo={combo}
                                    comboPotentialChoices={comboPotentialChoices}
                                    shapeFieldTypeOptions={shapeFieldTypeOptions}
                                    index={index}
                                    updatedCombinations={updatedCombinations}
                                    setUpdatedCombinations={setUpdatedCombinations}
                                    removeCombo={removeCombo}
                                />
                            ))}
                            <PrimaryButton
                                onClick={() => {
                                    addCombo();
                                }}
                                style={{ marginTop: 15 }}
                            >
                                Add Combo
                            </PrimaryButton>
                        </>
                    ) : (
                        <>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item xs={8}>
                                    <FormControl>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="field-select"
                                            value={shapeFieldType}
                                            onChange={handleFieldChange}
                                            MenuProps={{
                                                MenuListProps: {
                                                    sx: {
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        'li.MuiButtonBase-root': {
                                                            display: 'flex',
                                                            width: '100%',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {renderShapeFieldTypeMenuItems(shapeFieldTypeOptions)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid container item justifyContent="center" xs={4}>
                                    <Body1>Shape</Body1>
                                </Grid>
                            </Grid>
                            {shapesOverride?.map((entry) => (
                                <Grid
                                    key={entry.name}
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    css={css`
                                        margin-top: 0.5rem;
                                    `}
                                >
                                    <Grid
                                        item
                                        xs={8}
                                        css={css`
                                            word-break: break-word;
                                            overflow-wrap: anywhere;
                                            padding-right: 0.5rem;
                                        `}
                                    >
                                        <Body2>{entry.label || entry.name}</Body2>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={4}
                                        css={css`
                                            display: flex;
                                            justify-content: center;
                                        `}
                                    >
                                        <ShapeSelect
                                            entry={entry}
                                            shapesOverride={shapesOverride}
                                            setShapesOverride={setShapesOverride}
                                            showComboShapes={showComboShapes}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    css={css`
                        border-top: 1px solid lightgrey;
                    `}
                >
                    <PrimaryButtonFullW onClick={handleSave} style={{ marginTop: 15 }}>
                        Save
                    </PrimaryButtonFullW>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const ShapeControl = ({
    mapRef,
    shapesOverride,
    setShapesOverride,
    saveShapes,
    shapeFieldType,
    setShapeFieldType,
    shapeFieldTypeOptions,
    savedShapes,
    setLockShapeType,
    comboPotentialChoices,
    showComboShapes,
    setShowComboShapes,
    setComboShapesOverride,
    comboShapesOverride,
}) => {
    useEffect(() => {
        if (mapRef?.current) {
            const controls = mapRef.current.controls[window.google.maps.ControlPosition.RIGHT_TOP];
            controls.push(controlContainer);
            containerRoot.render(
                <ShapeControlContent
                    shapesOverride={shapesOverride}
                    setShapesOverride={setShapesOverride}
                    saveShapes={saveShapes}
                    shapeFieldType={shapeFieldType}
                    setShapeFieldType={setShapeFieldType}
                    shapeFieldTypeOptions={shapeFieldTypeOptions}
                    savedShapes={savedShapes}
                    setLockShapeType={setLockShapeType}
                    comboPotentialChoices={comboPotentialChoices}
                    showComboShapes={showComboShapes}
                    setShowComboShapes={setShowComboShapes}
                    setComboShapesOverride={setComboShapesOverride}
                    comboShapesOverride={comboShapesOverride}
                />
            );

            return () => {
                const idx = controls.indexOf(controlContainer);
                if (idx >= 0) {
                    controls.removeAt(idx);
                }
            };
        }
    }, [mapRef?.current, shapesOverride]);

    return null;
};
